import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useCommitProductsToManufacturing } from "../hooks/Starters/useCommitProductsToManufacturing";
import { useCommitReleasedProductsToManufacturing } from "../hooks/Starters/useCommitReleasedProductsToManufacturing";
import { useCommitSalesOrderMutation } from "../hooks/Starters/useCommitSalesOrderMutation";
import { useCommitServiceOrderMutation } from "../hooks/Starters/useCommitServiceOrderMutation";
import { useCommitToManufacturingMutation } from "../hooks/Starters/useCommitToManufacturingMutation";
import { useManufacturingSync } from "../hooks/Starters/useManufacturingSync";
import { useRollbackManufacturingSalesOrderLines } from "../hooks/Starters/useRollbackManufacturingSalesOrderLines";

interface ISnackbarState {
    message: string;
    open: boolean;
    severity: "success" | "error";
}

export default function ProcessTriggerPanel() {
    const navigate = useNavigate();

    // Sales Order state
    const [orderID, setOrderID] = useState("");

    // Service Order state
    const [serviceOrderID, setServiceOrderID] = useState("");

    // Manufacturing state
    const [mfgMItemID, setMfgMItemID] = useState("");

    // Products to Manufacturing state
    const [prodDesignID, setProdDesignID] = useState("");
    const [prodMItemID, setProdMItemID] = useState("");

    // Released Products to Manufacturing state
    const [relDesignID, setRelDesignID] = useState("");
    const [relMItemID, setRelMItemID] = useState("");

    // Manufacturing Sync state
    const [syncDesignID, setSyncDesignID] = useState("");
    const [syncMItemID, setSyncMItemID] = useState("");

    // Rollback Manufacturing SO Lines state
    const [rbMItemID, setRbMItemID] = useState("");
    const [rbIncludeD365Cleanup, setRbIncludeD365Cleanup] = useState(true);

    // Snackbar state
    const [snackbar, setSnackbar] = useState<ISnackbarState>({
        message: "",
        open: false,
        severity: "success",
    });

    const commitSalesOrder = useCommitSalesOrderMutation();
    const commitServiceOrder = useCommitServiceOrderMutation();
    const commitToManufacturing = useCommitToManufacturingMutation();
    const commitProducts = useCommitProductsToManufacturing();
    const commitReleasedProducts = useCommitReleasedProductsToManufacturing();
    const manufacturingSync = useManufacturingSync();
    const rollbackMfgSOLines = useRollbackManufacturingSalesOrderLines();

    const handleSuccess = (instanceId: string) => {
        setSnackbar({
            message: `Process started. Instance ID: ${instanceId}`,
            open: true,
            severity: "success",
        });
        navigate(`/monitor/${instanceId}`);
    };

    const handleError = (error: Error) => {
        setSnackbar({
            message: `Failed to start process: ${error.message}`,
            open: true,
            severity: "error",
        });
    };

    const handleCommitSalesOrder = () => {
        const id = parseInt(orderID, 10);
        if (isNaN(id) || id <= 0) return;

        commitSalesOrder.mutate(
            { orderID: id },
            {
                onSuccess: (instanceId) => handleSuccess(instanceId),
                onError: handleError,
            },
        );
    };

    const handleCommitServiceOrder = (forInvoice: boolean) => {
        const id = parseInt(serviceOrderID, 10);
        if (isNaN(id) || id <= 0) return;

        commitServiceOrder.mutate(
            { serviceOrderID: id, forInvoice },
            {
                onSuccess: (instanceId) => handleSuccess(instanceId),
                onError: handleError,
            },
        );
    };

    const handleCommitToManufacturing = () => {
        const id = parseInt(mfgMItemID, 10);
        if (isNaN(id) || id <= 0) return;

        commitToManufacturing.mutate(
            { mItemID: id },
            {
                onSuccess: (instanceId) => handleSuccess(instanceId),
                onError: handleError,
            },
        );
    };

    const handleCommitProducts = () => {
        const designId = parseInt(prodDesignID, 10);
        const mItemId = parseInt(prodMItemID, 10);
        if (isNaN(designId) || isNaN(mItemId) || designId <= 0 || mItemId <= 0)
            return;

        commitProducts.mutate(
            { designID: designId, mItemID: mItemId },
            {
                onSuccess: (instanceId) => handleSuccess(instanceId),
                onError: handleError,
            },
        );
    };

    const handleCommitReleasedProducts = () => {
        const designId = parseInt(relDesignID, 10);
        const mItemId = parseInt(relMItemID, 10);
        if (isNaN(designId) || isNaN(mItemId) || designId <= 0 || mItemId <= 0)
            return;

        commitReleasedProducts.mutate(
            { designID: designId, mItemID: mItemId },
            {
                onSuccess: (instanceId) => handleSuccess(instanceId),
                onError: handleError,
            },
        );
    };

    const handleManufacturingSync = () => {
        const designId = parseInt(syncDesignID, 10);
        const mItemId = parseInt(syncMItemID, 10);
        if (isNaN(designId) || isNaN(mItemId) || designId <= 0 || mItemId <= 0)
            return;

        manufacturingSync.mutate(
            { designID: designId, mItemID: mItemId },
            {
                onSuccess: (instanceId) => handleSuccess(instanceId),
                onError: handleError,
            },
        );
    };

    const handleRollbackManufacturingSalesOrderLines = () => {
        const id = parseInt(rbMItemID, 10);
        if (isNaN(id) || id <= 0) return;

        rollbackMfgSOLines.mutate(
            { mItemID: id, includeD365Cleanup: rbIncludeD365Cleanup },
            {
                onSuccess: (instanceId) => handleSuccess(instanceId),
                onError: handleError,
            },
        );
    };

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack alignItems="center" direction="row" spacing={1}>
                        <PlayArrowIcon color="primary" />
                        <Typography variant="subtitle1">
                            Trigger New Process
                        </Typography>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={3}>
                        {/* Commit Sales Order */}
                        <Box>
                            <Typography
                                gutterBottom
                                variant="subtitle2"
                            >
                                Commit Sales Order
                            </Typography>
                            <Stack alignItems="flex-start" direction="row" spacing={2}>
                                <TextField
                                    label="Order ID"
                                    onChange={(e) => setOrderID(e.target.value)}
                                    size="small"
                                    type="number"
                                    value={orderID}
                                />
                                <Button
                                    disabled={
                                        !orderID ||
                                        commitSalesOrder.isPending
                                    }
                                    loading={commitSalesOrder.isPending}
                                    onClick={handleCommitSalesOrder}
                                    variant="contained"
                                >
                                    Submit
                                </Button>
                            </Stack>
                        </Box>

                        {/* Commit Service Order */}
                        <Box>
                            <Typography
                                gutterBottom
                                variant="subtitle2"
                            >
                                Commit Service Order
                            </Typography>
                            <Stack alignItems="flex-start" direction="row" spacing={2}>
                                <TextField
                                    label="Service Order ID"
                                    onChange={(e) => setServiceOrderID(e.target.value)}
                                    size="small"
                                    type="number"
                                    value={serviceOrderID}
                                />
                                <Button
                                    disabled={
                                        !serviceOrderID ||
                                        commitServiceOrder.isPending
                                    }
                                    loading={commitServiceOrder.isPending}
                                    onClick={() => handleCommitServiceOrder(false)}
                                    variant="contained"
                                >
                                    Confirm
                                </Button>
                                <Button
                                    disabled={
                                        !serviceOrderID ||
                                        commitServiceOrder.isPending
                                    }
                                    loading={commitServiceOrder.isPending}
                                    onClick={() => handleCommitServiceOrder(true)}
                                    variant="contained"
                                >
                                    Confirm for Invoice
                                </Button>
                            </Stack>
                        </Box>

                        {/* Commit to Manufacturing */}
                        <Box>
                            <Typography
                                gutterBottom
                                variant="subtitle2"
                            >
                                Commit to Manufacturing
                            </Typography>
                            <Stack alignItems="flex-start" direction="row" spacing={2}>
                                <TextField
                                    label="MItem ID"
                                    onChange={(e) =>
                                        setMfgMItemID(e.target.value)
                                    }
                                    size="small"
                                    type="number"
                                    value={mfgMItemID}
                                />
                                <Button
                                    disabled={
                                        !mfgMItemID ||
                                        commitToManufacturing.isPending
                                    }
                                    loading={commitToManufacturing.isPending}
                                    onClick={handleCommitToManufacturing}
                                    variant="contained"
                                >
                                    Submit
                                </Button>
                            </Stack>
                        </Box>

                        {/* Manufacturing Sync */}
                        <Box>
                            <Typography
                                gutterBottom
                                variant="subtitle2"
                            >
                                Manufacturing Sync
                            </Typography>
                            <Stack alignItems="flex-start" direction="row" spacing={2}>
                                <TextField
                                    label="Design ID"
                                    onChange={(e) =>
                                        setSyncDesignID(e.target.value)
                                    }
                                    size="small"
                                    type="number"
                                    value={syncDesignID}
                                />
                                <TextField
                                    label="MItem ID"
                                    onChange={(e) =>
                                        setSyncMItemID(e.target.value)
                                    }
                                    size="small"
                                    type="number"
                                    value={syncMItemID}
                                />
                                <Button
                                    disabled={
                                        !syncDesignID ||
                                        !syncMItemID ||
                                        manufacturingSync.isPending
                                    }
                                    loading={manufacturingSync.isPending}
                                    onClick={handleManufacturingSync}
                                    variant="contained"
                                >
                                    Submit
                                </Button>
                            </Stack>
                        </Box>

                        {/* Commit Products to Manufacturing */}
                        <Box>
                            <Typography
                                gutterBottom
                                variant="subtitle2"
                            >
                                Commit Products to Manufacturing
                            </Typography>
                            <Stack alignItems="flex-start" direction="row" spacing={2}>
                                <TextField
                                    label="Design ID"
                                    onChange={(e) =>
                                        setProdDesignID(e.target.value)
                                    }
                                    size="small"
                                    type="number"
                                    value={prodDesignID}
                                />
                                <TextField
                                    label="MItem ID"
                                    onChange={(e) =>
                                        setProdMItemID(e.target.value)
                                    }
                                    size="small"
                                    type="number"
                                    value={prodMItemID}
                                />
                                <Button
                                    disabled={
                                        !prodDesignID ||
                                        !prodMItemID ||
                                        commitProducts.isPending
                                    }
                                    loading={commitProducts.isPending}
                                    onClick={handleCommitProducts}
                                    variant="contained"
                                >
                                    Submit
                                </Button>
                            </Stack>
                        </Box>

                        {/* Commit Released Products to Manufacturing */}
                        <Box>
                            <Typography
                                gutterBottom
                                variant="subtitle2"
                            >
                                Commit Released Products to Manufacturing
                            </Typography>
                            <Stack alignItems="flex-start" direction="row" spacing={2}>
                                <TextField
                                    label="Design ID"
                                    onChange={(e) =>
                                        setRelDesignID(e.target.value)
                                    }
                                    size="small"
                                    type="number"
                                    value={relDesignID}
                                />
                                <TextField
                                    label="MItem ID"
                                    onChange={(e) =>
                                        setRelMItemID(e.target.value)
                                    }
                                    size="small"
                                    type="number"
                                    value={relMItemID}
                                />
                                <Button
                                    disabled={
                                        !relDesignID ||
                                        !relMItemID ||
                                        commitReleasedProducts.isPending
                                    }
                                    loading={commitReleasedProducts.isPending}
                                    onClick={handleCommitReleasedProducts}
                                    variant="contained"
                                >
                                    Submit
                                </Button>
                            </Stack>
                        </Box>

                        {/* Rollback Manufacturing Sales Order Lines */}
                        <Box>
                            <Typography
                                gutterBottom
                                variant="subtitle2"
                            >
                                Rollback Manufacturing Sales Order Lines
                            </Typography>
                            <Stack alignItems="flex-start" direction="row" spacing={2}>
                                <TextField
                                    label="MItem ID"
                                    onChange={(e) => setRbMItemID(e.target.value)}
                                    size="small"
                                    type="number"
                                    value={rbMItemID}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={rbIncludeD365Cleanup}
                                            onChange={(e) =>
                                                setRbIncludeD365Cleanup(e.target.checked)
                                            }
                                        />
                                    }
                                    label="Include D365 Cleanup"
                                />
                                <Button
                                    disabled={
                                        !rbMItemID ||
                                        rollbackMfgSOLines.isPending
                                    }
                                    loading={rollbackMfgSOLines.isPending}
                                    onClick={handleRollbackManufacturingSalesOrderLines}
                                    variant="contained"
                                >
                                    Submit
                                </Button>
                            </Stack>
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Snackbar
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                open={snackbar.open}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
